import dayjs, { type Dayjs } from 'dayjs';
import { useGoodsOptionsBrief } from '../queries/useGoodsOptionsBrief';
import { useCallback } from 'react';
import { useConfigs, useMyInfo } from 'shared/src/queries';
import {
  getCurrentTimeDiff,
  getDayOfWeek,
  getHour,
  isNil,
} from 'shared/src/utils';

const RURAL_REGION_ADDITION_DAYS = 2;

const formatRuralArrival = (arrivalDate: Dayjs): string =>
  `${arrivalDate.format('M/D')}(${getDayOfWeek(arrivalDate)}) 도착`;

const getThirdPLOptionDeadlineText = (
  limitTime: Dayjs,
  isDefaultAddressRural: boolean,
): string => {
  // 23:59:59는 자정으로 취급 → 평일·토 자정 컷오프이므로 "컷오프 후" 상태가 발생하지 않음
  const isMidnight = limitTime.format('HH:mm:ss') === '23:59:59';
  const isTimeOver = !isMidnight && getCurrentTimeDiff(limitTime) > 0;

  if (isDefaultAddressRural) {
    // 컷오프 이전이면 내일 도착(today + 1), 이후면 모레 도착(today + 2) 가정 + 도서산간 +2일
    const baseOffsetDays = isTimeOver ? 2 : 1;
    const arrivalDay = dayjs().add(
      baseOffsetDays + RURAL_REGION_ADDITION_DAYS,
      'day',
    );
    const arrivalText = formatRuralArrival(arrivalDay);
    if (isMidnight) return `밤 12시 전 주문 시 ${arrivalText}`;
    if (isTimeOver) return `주문 마감으로 ${arrivalText}`;
    const timeText = limitTime.locale('ko').format('A h');
    return `${timeText}시 전 주문 시 ${arrivalText}`;
  }

  if (isMidnight) {
    return '밤 12시 전 주문 시 내일 도착';
  }

  // 컷오프 이후
  if (isTimeOver) {
    return '주문 마감으로 모레 도착';
  }

  // 컷오프 이전
  const timeText = limitTime.locale('ko').format('A h');
  return `${timeText}시 전 주문 시 내일 도착`;
};

export const useGetOrderDeadlineText = () => {
  const { data: optionsBrief } = useGoodsOptionsBrief();
  const { data: configs } = useConfigs();
  const { data: myInfo } = useMyInfo();
  const isDefaultAddressRural = myInfo?.is_default_address_rural === true;

  const getLimitTime = useCallback(
    (params: { delivery_type: 'shak' | 'today' | 'third_pl' }) => {
      if (isNil(optionsBrief)) return null;
      // configs?.shak_delivery.operation_end_time
      if (params.delivery_type === 'shak') {
        return getHour(configs?.shak_delivery.operation_end_time);
      }

      if (params.delivery_type === 'third_pl') {
        if (isNil(optionsBrief.third_pl_delivery_order_deadline_time)) {
          return null;
        }
        return getHour(optionsBrief.third_pl_delivery_order_deadline_time);
      }

      return getHour(optionsBrief.today_delivery_order_deadline_time);
    },
    [configs?.shak_delivery.operation_end_time, optionsBrief],
  );

  return useCallback<
    (params: {
      delivery_type: 'shak' | 'today' | 'third_pl' | 'standard';
    }) => string
  >(
    params => {
      if (isNil(optionsBrief)) {
        return '';
      }

      if (params.delivery_type === 'standard') {
        return '';
      }

      if (params.delivery_type === 'third_pl') {
        const limitTime = getLimitTime({ delivery_type: 'third_pl' });
        if (isNil(limitTime)) return '';

        return getThirdPLOptionDeadlineText(limitTime, isDefaultAddressRural);
      }

      const limitTime = getLimitTime({ delivery_type: params.delivery_type });
      if (isNil(limitTime)) return '';

      const diffSecond = getCurrentTimeDiff(limitTime);
      const isTimeOver = diffSecond > 0;

      if (isTimeOver) {
        return '주문 마감으로 내일 출발';
      }

      const hour = limitTime.hour();
      return `${hour < 12 ? '오전 ' : '오후 '}${hour > 12 ? hour - 12 : hour}시 전 주문하면 오늘 출발`;
    },
    [getLimitTime, optionsBrief, isDefaultAddressRural],
  );
};
